// src/hooks/useWebWorkerTimer.js
import { useRef, useCallback, useEffect, useState } from 'react';

const useWebWorkerTimer = () => {
  const workerRef = useRef(null);
  const onTimeUpRef = useRef(null);
  const onTickRef = useRef(null);
  
  // Only use state for major events that should trigger re-renders
  const [isActive, setIsActive] = useState(false);
  
  // All timer data in refs - NO state updates for timer values
  const currentTimeLeftRef = useRef(0);
  const currentProgressRef = useRef(100);

  // Initialize the worker
  useEffect(() => {
    console.log('🔧 Initializing timer worker...');
    
    try {
      workerRef.current = new Worker('/timer-worker.js');
      
      workerRef.current.onmessage = (e) => {
        const { type, ...data } = e.data;
        
        // Log all timer messages (but throttle tick messages)
        if (type !== 'tick') {
          console.log(`⏰ Timer Worker: ${type}`, data);
        }
        
        switch (type) {
          case 'tick':
            // ONLY update refs - NO state updates
            currentTimeLeftRef.current = data.timeLeft;
            currentProgressRef.current = data.progress;
            
            // Call tick callback for display updates if needed
            if (onTickRef.current) {
              onTickRef.current(data);
            }
            break;
            
          case 'timeUp':
            console.log('🚨 Timer ended! Calling game end callback...');
            setIsActive(false); // Only state update when timer ends
            currentTimeLeftRef.current = 0;
            currentProgressRef.current = 0;
            
            if (onTimeUpRef.current) {
              onTimeUpRef.current();
            }
            break;
            
          case 'started':
            console.log('▶️ Timer started successfully');
            setIsActive(true); // Only state update when timer starts
            break;
            
          case 'stopped':
            console.log('⏹️ Timer stopped');
            setIsActive(false); // Only state update when timer stops
            currentTimeLeftRef.current = 0;
            currentProgressRef.current = 0;
            break;
            
          case 'paused':
            console.log('⏸️ Timer paused');
            break;
            
          case 'resumed':
            console.log('▶️ Timer resumed');
            break;
            
          case 'state':
            // Update refs only
            currentTimeLeftRef.current = data.timeLeft;
            currentProgressRef.current = data.progress;
            setIsActive(data.isRunning);
            break;
            
          case 'error':
            console.error('💥 Timer worker error:', data.message);
            break;
            
          default:
            console.warn('❓ Unknown timer worker message:', type, data);
        }
      };
      
      workerRef.current.onerror = (error) => {
        console.error('💥 Timer worker error:', error);
      };
      
      console.log('✅ Timer worker initialized successfully');
      
    } catch (error) {
      console.error('💥 Failed to create timer worker:', error);
    }

    return () => {
      console.log('🧹 Cleaning up timer worker...');
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  const sendMessage = useCallback((type, data = {}) => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type, data });
    }
  }, []);

  const start = useCallback((duration, onTimeUp = null, onTick = null) => {
    console.log('🚀 Timer start called with duration:', duration);
    onTimeUpRef.current = onTimeUp;
    onTickRef.current = onTick;
    currentTimeLeftRef.current = duration;
    currentProgressRef.current = 100;
    sendMessage('start', { duration });
  }, [sendMessage]);

  const pause = useCallback(() => {
    sendMessage('pause');
  }, [sendMessage]);

  const resume = useCallback(() => {
    sendMessage('resume');
  }, [sendMessage]);

  const stop = useCallback(() => {
    sendMessage('stop');
    onTimeUpRef.current = null;
    onTickRef.current = null;
    currentTimeLeftRef.current = 0;
    currentProgressRef.current = 0;
  }, [sendMessage]);

  const getState = useCallback(() => {
    sendMessage('getState');
  }, [sendMessage]);

  // Helper function to get current time left synchronously (for game logic)
  const getCurrentTimeLeft = useCallback(() => {
    return currentTimeLeftRef.current;
  }, []);

  // Helper function to get current progress (for display)
  const getCurrentProgress = useCallback(() => {
    return currentProgressRef.current;
  }, []);

  return {
    // Controls
    start,
    pause,
    resume,
    stop,
    getState,
    
    // State (only major events that should trigger re-renders)
    isActive,
    
    // Getters for current values (use these for game logic)
    getCurrentTimeLeft,
    getCurrentProgress
  };
};

export default useWebWorkerTimer;